import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import './index.css';

const WalkIns = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue, // ✅ Needed to sync collegeCode manually
    trigger, // ✅ Needed for manual validation
    clearErrors // ✅ Needed to clear validation errors
  } = useForm();

  const api = process.env.REACT_APP_API;

  const [collegeInput, setCollegeInput] = useState('');
  const [codeMap, setCodeMap] = useState({});
  const [projectType, setProjectType] = useState(''); // 'existing' or 'new'
  const [existingProjects, setExistingProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [center, setCenter] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [councillors, setCouncillors] = useState([]);
  const [customCouncillors, setCustomCouncillors] = useState([]);
  const [otherCouncillor, setOtherCouncillor] = useState('');
  const [showOtherCouncillor, setShowOtherCouncillor] = useState(false);
  const centers = ['DWK', 'MVP', 'GWK', 'HO']; // Available centers

  // Fetch code-name map from backend
  useEffect(() => {
    const fetchCodeMap = async () => {
      try {
        const res = await fetch(`${api}/college-code-map`);
        const data = await res.json();
        const map = {};
        data.forEach(({ code, name }) => {
          map[code.toUpperCase()] = name;
        });
        setCodeMap(map);
      } catch (error) {
        console.error('Failed to fetch college code map:', error);
      }
    };

    fetchCodeMap();
  }, []);

  // Fetch councillors
  useEffect(() => {
    const fetchCouncillors = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        const res = await fetch(`${api}/employees`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        setCouncillors(data.employees || []);
      } catch (error) {
        console.error('Failed to fetch councillors:', error);
      }
    };

    fetchCouncillors();
  }, []);

  // Handle councillor selection change
  const handleCouncillorChange = (value) => {
    if (value === 'other') {
      setShowOtherCouncillor(true);
      setValue('councillorName', '', { shouldValidate: false });
      clearErrors('councillorName'); // Clear any existing errors
    } else {
      setShowOtherCouncillor(false);
      setValue('councillorName', value, { shouldValidate: true });
      setOtherCouncillor('');
      trigger('councillorName'); // Re-validate
    }
  };

  // Fetch existing projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        const res = await fetch(`${api}/project-admissions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (res.ok) {
          const data = await res.json();
          // Get unique project names and sort them
          const uniqueProjects = [...new Set(data.map(p => p.projectName).filter(Boolean))].sort();
          setExistingProjects(uniqueProjects);
          console.log('Fetched projects:', uniqueProjects.length, uniqueProjects);
        } else {
          console.error('Failed to fetch projects:', res.status, res.statusText);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };

    fetchProjects();
  }, [api]);

  // Auto-fill or clear college name when college code changes
  useEffect(() => {
    const cleanedCode = (collegeInput || '').trim().toUpperCase();
    const name = codeMap[cleanedCode];

    reset((formValues) => ({
      ...formValues,
      collegeName: name || '',
    }));
  }, [collegeInput, codeMap, reset]);

  const filteredOptions = Object.keys(codeMap).filter((code) =>
    code.toLowerCase().startsWith(collegeInput.toLowerCase())
  );

  const onSubmit = async (data) => {
    const code = (data.collegeCode || '').trim().toUpperCase();
    const name = (data.collegeName || '').trim();

    console.log('Sending college code:', { code, name });

    // Handle custom councillor
    let councillorName = data.councillorName || '';
    if (showOtherCouncillor && otherCouncillor) {
      // Add new councillor to custom list
      setCustomCouncillors(prev => [...prev, otherCouncillor]);
      councillorName = otherCouncillor;
    }

    // Set project title based on selection
    let finalProjectTitle = data.projectTitle || '';
    if (projectType === 'existing' && selectedProject) {
      finalProjectTitle = selectedProject;
    } else if (projectType === 'new') {
      finalProjectTitle = data.projectTitle || '';
    }

    // Explicitly construct the payload with all fields
    const walkinPayload = {
      studentName: data.studentName || '',
      collegeCode: code || '',
      collegeName: name || data.collegeName || '',
      reference: data.reference || '',
      councillorName: councillorName || '',
      projectTitle: finalProjectTitle,
      projectType: projectType || '', // Always include projectType
      center: center || '', // Always include center (even if empty)
      mobileNumber: data.mobileNumber || '',
      status: data.status || 'not joined',
      remark: data.remark || '', // Always include remark (even if empty)
      remarks: [] // Initialize empty remarks array
    };

    console.log('Walkin payload being sent:', walkinPayload);

    try {
      // Always send code-name pair to backend (for insert or update)
      await fetch(`${api}/college-code-map`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, name }),
      });

      // Submit walk-in form
      const response = await fetch(`${api}/walkins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(walkinPayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to save walkin' }));
        throw new Error(errorData.message || 'Failed to save walkin');
      }

      const savedWalkin = await response.json();
      console.log('Walkin saved successfully:', savedWalkin);

      alert('Registered Successfully');
      reset();
      setCollegeInput('');
      setProjectType('');
      setSelectedProject('');
      setProjectSearchTerm('');
      setCenter('');
    } catch (err) {
      console.error('Error submitting:', err);
      alert('Failed to register: ' + (err.message || 'Unknown error'));
    }
  };

  // Filter existing projects based on search term
  const filteredProjects = projectSearchTerm.trim() === '' 
    ? existingProjects 
    : existingProjects.filter(project =>
        project && project.toLowerCase().includes(projectSearchTerm.toLowerCase())
      );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="student-form">
      <div className="form-group">
        <label>Student Name <span className="required">*</span></label>
        <input type="text" {...register('studentName', { required: true })} />
        {errors.studentName && <p className="error">Student Name is required</p>}
      </div>

      {/* <div className="form-group">
        <label>College Code <span className="required">*</span></label>
        <input
          type="text"
          list="collegeCodeList"
          value={collegeInput}
          {...register('collegeCode', { required: true })}
          onChange={(e) => {
            setCollegeInput(e.target.value);
            setValue('collegeCode', e.target.value); // ✅ Sync with form
          }}
        />
        <datalist id="collegeCodeList">
          {filteredOptions.map((code) => (
            <option key={code} value={code} />
          ))}
        </datalist>
        {errors.collegeCode && <p className="error">College Code is required</p>}
      </div> */}

      <div className="form-group">
        <label>College Name <span className="required">*</span></label>
        <input
          type="text"
          {...register('collegeName', { required: true })}
          readOnly={Boolean(codeMap[(collegeInput || '').trim().toUpperCase()])}
        />
        {errors.collegeName && <p className="error">College Name is required</p>}
      </div>

      <div className="form-group">
        <label>Reference</label>
        <input type="text" {...register('reference')} />
      </div>

      <div className="form-group">
        <label>Councillor Name <span className="required">*</span></label>
        <select 
          {...register('councillorName', { required: true })} 
          onChange={(e) => handleCouncillorChange(e.target.value)}
        >
          <option value="">Select Councillor</option>
          {councillors.filter(c => c.role === 'Councillor').map((councillor, index) => (
            <option key={index} value={councillor.username}>
              {councillor.username}
            </option>
          ))}
          <option value="Mahalakshmi">Mahalakshmi</option>
          <option value="Maghana">Maghana</option>
          <option value="Ishak">Ishak</option>
          {customCouncillors.map((councillor, index) => (
            <option key={`custom-${index}`} value={councillor}>
              {councillor}
            </option>
          ))}
          <option value="other">Other (Enter New Name)</option>
        </select>
        {errors.councillorName && !showOtherCouncillor && <p className="error">Councillor Name is required</p>}
        
        {showOtherCouncillor && (
          <div className="form-group" style={{ marginTop: '10px' }}>
            <label>Enter New Councillor Name <span className="required">*</span></label>
            <input 
              type="text" 
              value={otherCouncillor}
              onChange={(e) => {
                const value = e.target.value;
                setOtherCouncillor(value);
                setValue('councillorName', value, { shouldValidate: true });
                if (value.trim()) {
                  clearErrors('councillorName'); // Clear error when field has value
                }
              }}
              placeholder="Enter new councillor name"
            />
            {errors.councillorName && showOtherCouncillor && <p className="error">Councillor Name is required</p>}
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Project Type</label>
        <select 
          value={projectType} 
          onChange={(e) => {
            setProjectType(e.target.value);
            setSelectedProject('');
            setProjectSearchTerm('');
            setValue('projectTitle', '');
          }}
        >
          <option value="">Select Project Type</option>
          <option value="existing">Already Existing</option>
          <option value="new">New Project</option>
        </select>
      </div>

      {projectType === 'existing' && (
        <div className="form-group">
          <label>Select Existing Project <span className="required">*</span></label>
          <div className="project-filter-container">
            <input
              type="text"
              placeholder="Search projects..."
              value={projectSearchTerm}
              onChange={(e) => setProjectSearchTerm(e.target.value)}
              className="project-search-input"
            />
            <select 
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value);
                setValue('projectTitle', e.target.value);
                setProjectSearchTerm(''); // Clear search after selection
              }}
              required={projectType === 'existing'}
              className="project-select-dropdown"
            >
              <option value="">
                Select Project {projectSearchTerm && `(Filtered: ${filteredProjects.length})`}
              </option>
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project, index) => (
                  <option key={index} value={project}>{project}</option>
                ))
              ) : projectSearchTerm.trim() !== '' ? (
                <option value="" disabled>No projects found matching "{projectSearchTerm}"</option>
              ) : (
                <option value="" disabled>No projects available</option>
              )}
            </select>
            {existingProjects.length > 0 && (
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {filteredProjects.length} of {existingProjects.length} projects
                {projectSearchTerm && ` matching "${projectSearchTerm}"`}
              </small>
            )}
          </div>
        </div>
      )}

      {projectType === 'new' && (
        <div className="form-group">
          <label>Project Title</label>
          <input 
            type="text" 
            {...register('projectTitle')} 
            placeholder="Enter new project title"
          />
        </div>
      )}

      <div className="form-group">
        <label>Center</label>
        <select 
          value={center}
          onChange={(e) => setCenter(e.target.value)}
        >
          <option value="">Select Center</option>
          {centers.map((c, index) => (
            <option key={index} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Mobile Number <span className="required">*</span></label>
        <input
          type="text"
          {...register('mobileNumber', {
            required: true,
            pattern: /^[0-9]{10}$/,
          })}
        />
        {errors.mobileNumber && (
          <p className="error">
            {errors.mobileNumber.type === 'required'
              ? 'Mobile Number is required'
              : 'Mobile Number must be 10 digits'}
          </p>
        )}
      </div>

      <div className="form-group">
        <label>Remark</label>
        <textarea
          {...register('remark')}
          rows="3"
          placeholder="Enter any initial remarks or notes..."
        />
      </div>

      <button type="submit">Submit</button>
    </form>
  );
};

export default WalkIns;
